// @ts-strict-ignore
import {AttrsSchema} from 'panel';
import React from 'react';
import {Component} from 'panel';

import {SectionItem} from 'common/components/items-menu/types';
import {Placement} from 'common/components/tooltip/types';
import {formatText} from 'common/util/format-text';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Directions, PositioningStrategy} from 'common/components/drop-menu/types';
import {RevealableTextEditor} from 'common/components/revealable-text-editor';
import {IronContextRegistry} from 'common/report/context-registry';

import css from './index.inline.styl';
// eslint-disable-next-line import/no-cycle
import {template} from './template';
import {ContextPanelData} from './types';

export type ContextPanelAttrs = {
  'project-is-free-plan'?: boolean;
  placement?: Directions;
  'force-placement'?: boolean;
  'position-strategy'?: PositioningStrategy;
  offset?: number;
  skidding?: number;
  'show-arrow'?: boolean;
  'always-expanded'?: boolean;
};

type ContextPanelParams = {
  data?: ContextPanelData;
  positionParent?: HTMLElement;
};

export type ContextPanelProps = {
  params?: ContextPanelParams;
  attrs?: ContextPanelAttrs;
  on?: {
    hoverIn?: () => void;
    hoverOut?: () => void;
    tooltipContextAction?: (ev: any) => void;
    tooltipContextEditDescription?: (ev: any) => void;
  };
};

type ContextPanelState = {
  description: string;
  expanded: boolean;
  openMoreDropdown: boolean;
};

export class ContextPanel extends Component<
  ContextPanelState,
  ContextPanelAttrs,
  EmptyObj,
  EmptyObj,
  IronContextRegistry,
  ContextPanelParams
> {
  private declare textEditor: RevealableTextEditor;

  static get attrsSchema(): AttrsSchema<ContextPanelAttrs> {
    return {
      ...super.attrsSchema,
      'project-is-free-plan': `boolean`,
      placement: {
        type: `string`,
        default: Directions.Right,
        enum: Object.values(Directions),
      },
      'position-strategy': {
        type: `string`,
        enum: Object.values(PositioningStrategy),
        default: PositioningStrategy.Absolute,
        description: `Defines how the tooltip is rendered, fixed to the document level or absolute relative to its offset parent`,
      },
      'force-placement': `boolean`,
      offset: {type: `number`, default: 2},
      skidding: {type: `number`, default: 0},
      'show-arrow': {type: `boolean`, description: `Whether or not to show a tooltip arrow`},
      'always-expanded': {
        type: `boolean`,
        description: `Remove the "Show more" button and always show all information`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`urlManager` as const, `i18n` as const],
      defaultState: {
        description: ``,
        expanded: false,
        openMoreDropdown: false,
      },
      params: {
        data: Object,
        positionParent: Object,
      },
      helpers: {
        formatText,

        isTooltipDataSet: (): boolean => {
          const tooltipData = this.params.data;
          return !!tooltipData && Object.values(tooltipData).some((v) => (Array.isArray(v) ? !!v.length : !!v));
        },

        handleTextEditorSave: (ev, item) => {
          const description = ev.detail.value;
          this.dispatchEvent(
            new CustomEvent(`tooltipContextEditDescription`, {
              composed: true,
              cancelable: true,
              detail: {description, item},
            }),
          );
          this.update({description});
        },

        handleExpand: () => {
          this.update({expanded: true});
        },

        getActionItems: () => {
          const {actions} = this.params.data;
          const actionItems: SectionItem[] = [];

          actions?.forEach((action) => {
            actionItems.push({
              label: action.label,
              value: action.name,
              icon: action.icon,
              item: action.item,
              isDisabled: action.disabled,
              ...(action.disabled && action.disabledTooltipText
                ? {
                    tooltip: {
                      text: action.disabledTooltipText,
                      placement: Placement.Left,
                    },
                  }
                : {}),
            });
          });

          return actionItems;
        },

        handleMouseEnter: () => {
          this.dispatchEvent(new CustomEvent(`hoverIn`));
        },

        handleActionClick: (actionItem) => {
          this.dispatchEvent(
            new CustomEvent(`tooltipContextAction`, {
              composed: true,
              cancelable: true,
              detail: {
                name: actionItem.value,
                item: actionItem.item,
              },
            }),
          );
        },

        handleMouseLeave: () => {
          this.dispatchEvent(new CustomEvent(`hoverOut`));
        },

        editorInserted: (vnode) => {
          this.textEditor = vnode.elm;
        },

        actionClick: (action) => {
          this.dispatchEvent(
            new CustomEvent(`tooltipContextAction`, {
              composed: true,
              cancelable: true,
              detail: {
                name: action.name,
                item: action.item,
              },
            }),
          );
        },

        linkClick: (link) => window.open(link.href, `_blank`),
      },
      hooks: {
        postUpdate: (_state, paramsUpdate?: ContextPanelParams) => {
          if (paramsUpdate?.data) {
            //TODO [shayan] find a better way
            if (this.textEditor) {
              this.textEditor.cancelEdit();
            }
            this.setDescription();
            this.closeMoreDropdown();
          }
        },
      },
    };
  }

  private setDescription() {
    if (this.params.data) {
      this.update({description: this.getContext(`i18n`).print(this.params.data.description)});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.setDescription();
    this.closeMoreDropdown();
  }

  openMoreDropdown() {
    this.update({openMoreDropdown: true});
  }

  closeMoreDropdown() {
    this.update({openMoreDropdown: false});
  }

  getUrlManager() {
    return this.getContext(`urlManager`);
  }
}

export const ContextPanelEl = defineAndMakeJsxFactory<ContextPanelProps>(`mp-context-panel`, ContextPanel);
export const ContextPanelReact = panel2react<ContextPanelProps>(`mp-context-panel`, ContextPanel);

export type ContextPanelReactProps = React.ComponentProps<typeof ContextPanelReact>;
